import type { ReactNode } from "react";
import type { SlideProps } from "@/engine/types";
import { GlassCard, GradientText, Reveal, RevealItem, RevealList, SlideShell } from "@/ui";

const PILLARS = [
  "on-page",
  "off-page",
  "technical",
  "local",
  "mobile",
  "content",
  "commerce",
] as const;

const CARDS: { n: number; body: ReactNode; extra?: ReactNode }[] = [
  {
    n: 1,
    body: (
      <>
        <GradientText className="font-semibold">Authority</GradientText> and{" "}
        <span className="font-semibold text-white">trust</span> — tools vs reality — plus the
        pillars:
      </>
    ),
    extra: (
      <div className="flex flex-wrap gap-2.5">
        {PILLARS.map((p) => (
          <span
            key={p}
            className="rounded-full border border-white/10 bg-white/5 px-4 py-1.5 font-mono text-xl text-white/70"
          >
            {p}
          </span>
        ))}
      </div>
    ),
  },
  {
    n: 2,
    body: (
      <>
        <span className="font-semibold text-white">Keywords</span> and{" "}
        <span className="font-semibold text-white">long-tail</span>,{" "}
        <span className="font-semibold text-white">GSC</span> patterns,{" "}
        <span className="font-semibold text-white">SERP</span> expectations, and{" "}
        <GradientText className="font-semibold">hat labels</GradientText>.
      </>
    ),
    extra: (
      <p className="text-xl leading-relaxed text-accent-amber/90">
        Risks, not a shortcut menu.
      </p>
    ),
  },
  {
    n: 3,
    body: (
      <>
        <span className="font-semibold text-white">Defense</span> against{" "}
        <span className="font-semibold text-white">abuse</span> and an intro to{" "}
        <GradientText className="font-semibold">quality raters</GradientText>.
      </>
    ),
  },
];

export function RecapSlide({ active }: SlideProps) {
  return (
    <SlideShell chapter={7} title="Recap: what you covered">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-10">
        <RevealList active={active} stagger={0.3} delay={0.2} className="grid grid-cols-3 gap-8">
          {CARDS.map((card) => (
            <RevealItem key={card.n} className="h-full">
              <GlassCard className="flex h-full flex-col gap-5 px-8 py-7 transition-transform duration-300 hover:-translate-y-1">
                <span className="font-display text-6xl font-semibold leading-none">
                  <GradientText>{card.n}</GradientText>
                </span>
                <p className="text-2xl leading-relaxed text-white/90">{card.body}</p>
                {card.extra && <div className="mt-auto">{card.extra}</div>}
              </GlassCard>
            </RevealItem>
          ))}
        </RevealList>

        <Reveal active={active} delay={1.2}>
          <GlassCard glow className="px-10 py-6 text-center">
            <p className="text-3xl text-white/90">
              Helpful content stays the{" "}
              <GradientText className="font-semibold">through-line</GradientText>.
            </p>
          </GlassCard>
        </Reveal>
      </div>
    </SlideShell>
  );
}
